import React, { useEffect, useState, useRef } from 'react';
import LoadError from '../../components/LoadError';
import { useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete, apiUpload, apiDownload, API_BASE } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Building2, Plus, Search, Pencil, Trash2, Globe, MapPin, Trophy, X, Filter, Upload, Download, GraduationCap, BookOpen, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ProgramsDialog from '../../components/ProgramsDialog';
const STREAMS = ['Bachelor', 'Masters', 'UG', 'PG', 'Dip', 'Pathway Programs'];
const emptyUni = { name: '', country: '', city: '', ranking: 0, stream: '', courses: [], intake_months: [], requirements: '', website: '', description: '', tuition_range: '', cover_image_url: '' };

const getImageUrl = (url) => {
  if (!url) return '/university-fallback.jpg';
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return url;
};

const QUICK_COUNTRIES = [
  'USA', 'UK', 'Australia', 'Canada', 'Germany', 'France', 'Ireland',
  'New Zealand', 'Singapore', 'Malaysia', 'Dubai', 'Netherlands',
  'Switzerland', 'Italy', 'Spain', 'Poland', 'Latvia', 'Japan',
];

export default function UniversityManagement() {
  const [searchParams] = useSearchParams();
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState(() => searchParams.get('country') || 'all');
  const [streamFilter, setStreamFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [countries, setCountries] = useState([]);
  const [coursesList, setCoursesList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUni);
  const [coursesInput, setCoursesInput] = useState('');
  const [intakeInput, setIntakeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [fetchingImages, setFetchingImages] = useState(false);
  const [displayCount, setDisplayCount] = useState(24);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [importingPrograms, setImportingPrograms] = useState(false);
  const [programsUni, setProgramsUni] = useState(null);
  const [loadingProgramsId, setLoadingProgramsId] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const fileInputRef = useRef(null);
  const syncFileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const programsFileInputRef = useRef(null);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await apiUpload('/universities/upload-cover', formData);
      const newUrl = res.data.url;
      setForm(prev => ({ ...prev, cover_image_url: newUrl }));
      if (editing) {
        setUniversities(prev => prev.map(u => u.id === editing.id ? { ...u, cover_image_url: newUrl } : u));
      }
      toast.success('Cover photo uploaded successfully! Click Update to save changes.');
    } catch (error) {
      toast.error('Failed to upload cover photo');
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const fetchCountries = () => {
    apiGet('/universities/countries').then(r => setCountries(r.data)).catch(console.error);
  };

  const fetchCourses = () => {
    apiGet('/universities/courses').then(r => setCoursesList(r.data)).catch(console.error);
  };

  // `signal` is only passed by the auto-refetch effect below, which cancels
  // the in-flight request the moment the filters change again. Without that,
  // a slow response for a country you've already clicked away from can
  // resolve AFTER the next one and overwrite the screen with the wrong
  // country's list/count — exactly the "sometimes shows other countries
  // count" symptom. Manual calls after save/delete/import still work as
  // plain `fetchUniversities()`.
  const fetchUniversities = (signal) => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (countryFilter && countryFilter !== 'all') params.set('country', countryFilter);
    if (streamFilter && streamFilter !== 'all') params.set('stream', streamFilter);
    if (courseFilter && courseFilter !== 'all') params.set('course', courseFilter);
    const qs = params.toString();
    setLoading(true);
    setLoadError(false);
    apiGet(`/universities${qs ? '?' + qs : ''}`, signal ? { signal } : {})
      .then(r => setUniversities(r.data))
      .catch(err => {
        // A cancelled (stale) request — the newer one already owns `loading`
        // and `universities`, so just ignore this one instead of erroring.
        if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
        setLoadError(true);
      })
      .finally(() => {
        if (!(signal && signal.aborted)) setLoading(false);
      });
  };

  useEffect(() => { fetchCountries(); fetchCourses(); }, []);

  // Debounce the search box — without this every keystroke fired a full
  // /universities request (and for a country like UK/Canada that payload
  // embeds tens of thousands of program rows), which is what made typing
  // feel sluggish and stuck.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    fetchUniversities(controller.signal);
    return () => controller.abort();
  }, [debouncedSearch, countryFilter, streamFilter, courseFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyUni);
    setCoursesInput('');
    setIntakeInput('');
    setDialogOpen(true);
  };

  const openEdit = (uni) => {
    setEditing(uni);
    setForm({ ...uni });
    setCoursesInput((uni.courses || []).join(', '));
    setIntakeInput((uni.intake_months || []).join(', '));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.country) { toast.error('Name and country are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, courses: coursesInput.split(',').map(s => s.trim()).filter(Boolean), intake_months: intakeInput.split(',').map(s => s.trim()).filter(Boolean), ranking: parseInt(form.ranking) || 0 };
      if (editing) {
        await apiPut(`/universities/${editing.id}`, payload);
        toast.success('University updated');
      } else {
        await apiPost('/universities', payload);
        toast.success('University added');
      }
      setDialogOpen(false);
      fetchUniversities();
      fetchCountries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  // The list response no longer carries each university's full `programs`
  // array (see list_universities on the backend) — just a `programs_count`
  // for the badge. Fetch the one university's full detail (including
  // programs) only when its "View Programs" button is actually clicked.
  const openPrograms = async (uni) => {
    setLoadingProgramsId(uni.id);
    try {
      const res = await apiGet(`/universities/${uni.id}`);
      setProgramsUni(res.data);
    } catch (err) {
      toast.error('Failed to load programs');
    } finally {
      setLoadingProgramsId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this university?')) return;
    try {
      await apiDelete(`/universities/${id}`);
      toast.success('University deleted');
      fetchUniversities();
    } catch (err) { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="university-management">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>Universities</h1>
            <p className="text-ink-2 mt-1">Manage partner universities worldwide</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {/* Hidden file input for the per-course program catalog (Course Name,
              Campus, Degree Level, Tuition Fee, Duration, Active Intake(s)) */}
          <input
            ref={programsFileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              const country = window.prompt(
                "Country to use for any universities in this file that don't already exist (existing universities keep their current country):",
                countryFilter !== 'all' ? countryFilter : ''
              );
              if (country === null) return; // cancelled
              // Opt-in: by default this only adds/updates universities found in the
              // file (nothing is ever removed). Confirming here also deletes any
              // existing university in this same country that ISN'T in the file —
              // use this when the file should become the complete list for that country.
              const prune = window.confirm(
                `Also REMOVE any existing ${country || 'this country'} universities that are NOT in this file, so the list exactly matches the file?\n\nOK = replace the full ${country || 'country'} list with this file.\nCancel = just add/update the universities in this file, leave everything else as-is.`
              );
              setImportingPrograms(true);
              try {
                const formData = new FormData();
                formData.append('file', file);
                const params = new URLSearchParams({ country, prune: String(prune) });
                const res = await apiUpload(`/universities/import-programs?${params.toString()}`, formData);
                const d = res.data;
                const deleteMsg = d.universities_deleted > 0 ? `, ${d.universities_deleted} removed` : '';
                toast.success(`Imported ${d.total_programs} programs across ${d.universities_created + d.universities_updated} universities (${d.universities_created} new, ${d.universities_updated} updated${deleteMsg})`);
                fetchUniversities();
                fetchCountries();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to import programs');
              } finally {
                setImportingPrograms(false);
              }
            }}
          />
          <Button
            onClick={() => programsFileInputRef.current?.click()}
            disabled={importingPrograms}
            data-testid="import-programs-btn"
            variant="outline"
            className="rounded-lg gap-2 border-ember text-ember hover:bg-ember/10"
          >
            <GraduationCap className="w-4 h-4" /> {importingPrograms ? 'Importing...' : 'Import Programs'}
          </Button>
          <Button
            onClick={async () => {
              // For the scrape-country-by-country workflow: once a country's
              // data has been scraped and verified, this clears out every
              // other country's universities (and their programs) so the app
              // only ever shows countries that have actually been checked —
              // instead of a mix of verified data alongside old/unverified
              // seed data for countries not yet gone through.
              const input = window.prompt(
                'Delete ALL universities (and their programs) for every country EXCEPT the ones you list here — enter the countries to KEEP, comma-separated:',
                'Australia, Latvia'
              );
              if (input === null) return; // cancelled
              const keep = input.split(',').map(s => s.trim()).filter(Boolean);
              if (!keep.length) { toast.error('Enter at least one country to keep'); return; }
              const confirmed = window.confirm(
                `This will PERMANENTLY delete every university (and its programs) whose country is NOT one of: ${keep.join(', ')}.\n\nThis cannot be undone. Continue?`
              );
              if (!confirmed) return;
              setCleaningUp(true);
              try {
                const params = new URLSearchParams({ countries: keep.join(',') });
                const res = await apiPost(`/universities/keep-only-countries?${params.toString()}`);
                const d = res.data;
                const byCountry = Object.entries(d.deleted_by_country || {}).map(([c, n]) => `${c}: ${n}`).join(', ');
                toast.success(`Kept ${d.kept_count} universities (${d.kept_countries.join(', ')}). Deleted ${d.total_deleted}${byCountry ? ` (${byCountry})` : ''}.`);
                fetchUniversities();
                fetchCountries();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Cleanup failed');
              } finally {
                setCleaningUp(false);
              }
            }}
            disabled={cleaningUp}
            data-testid="keep-only-countries-btn"
            variant="outline"
            className="rounded-lg gap-2 border-ember text-danger hover:bg-ember/10"
          >
            <Trash2 className="w-4 h-4" /> {cleaningUp ? 'Cleaning...' : 'Keep Only...'}
          </Button>
          <Button onClick={openCreate} data-testid="add-university-btn" className="bg-brand hover:bg-brand/90 text-white rounded-lg gap-2">
            <Plus className="w-4 h-4" /> Add University
          </Button>
        </div>
      </div>

      {/* Quick country filter tiles */}
      {countries.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {[...QUICK_COUNTRIES.filter(qc => countries.some(c => c.toLowerCase() === qc.toLowerCase())), ...countries.filter(c => c && !QUICK_COUNTRIES.some(qc => qc.toLowerCase() === c.toLowerCase()))].map(qc => {
            const active = countryFilter.toLowerCase() === qc.toLowerCase();
            return (
              <button
                key={qc}
                onClick={() => setCountryFilter(active ? 'all' : qc)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  active
                    ? 'bg-brand text-white border-brand shadow-sm'
                    : 'bg-surface text-ember border-contrast/10 hover:border-brand hover:text-brand hover:bg-surface-2'
                }`}
              >
                <span>{qc}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Country / Stream / Course filters, then Search */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
        <Select value={countryFilter} onValueChange={setCountryFilter}>
          <SelectTrigger className="w-full sm:w-52 h-11" data-testid="country-filter" aria-label="Filter by country">
            <Filter className="w-4 h-4 mr-2 text-ink-3" />
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Country</SelectItem>
            {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={streamFilter} onValueChange={setStreamFilter}>
          <SelectTrigger className="w-full sm:w-56 h-11" data-testid="stream-filter" aria-label="Filter by level of education">
            <GraduationCap className="w-4 h-4 mr-2 text-ink-3" />
            <SelectValue placeholder="Level of Education" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Level of Education</SelectItem>
            {STREAMS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={courseFilter} onValueChange={setCourseFilter}>
          <SelectTrigger className="w-full sm:w-52 h-11" data-testid="course-filter" aria-label="Filter by program">
            <BookOpen className="w-4 h-4 mr-2 text-ink-3" />
            <SelectValue placeholder="Program" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Program</SelectItem>
            {coursesList.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-md sm:ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search universities..."
            data-testid="university-search-input"
            className="pl-10 h-11 border-contrast/10"
          />
        </div>
        {(countryFilter !== 'all' || streamFilter !== 'all' || courseFilter !== 'all') && (
          <div className="flex items-center gap-2 flex-wrap">
            {countryFilter !== 'all' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-surface-3 text-brand rounded-lg text-sm font-medium">
                {countryFilter}
                <button onClick={() => setCountryFilter('all')} aria-label="Clear country filter" className="hover:text-brand-soft"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {streamFilter !== 'all' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-ember/10 text-warning rounded-lg text-sm font-medium">
                {streamFilter}
                <button onClick={() => setStreamFilter('all')} aria-label="Clear education level filter" className="hover:text-warning"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {courseFilter !== 'all' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-brand/10 text-success rounded-lg text-sm font-medium">
                {courseFilter}
                <button onClick={() => setCourseFilter('all')} aria-label="Clear program filter" className="hover:text-success"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* University count */}
      <div className="flex items-center gap-2 text-sm text-ink-2">
        <Building2 className="w-4 h-4" />
        <span>{universities.length} universit{universities.length === 1 ? 'y' : 'ies'}{countryFilter !== 'all' ? ` in ${countryFilter}` : ''}</span>
      </div>

      {/* University Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" /></div>
      ) : loadError ? (
        <LoadError what="the universities" onRetry={() => fetchUniversities()} />
      ) : universities.length === 0 ? (
        <div className="bg-surface rounded-xl border border-contrast/[0.07] shadow-card p-12 text-center">
          <Building2 className="w-12 h-12 text-ink-3 mx-auto mb-4" />
          <p className="text-ink-2">{debouncedSearch || countryFilter !== 'all' || streamFilter !== 'all' || courseFilter !== 'all'
            ? 'No universities match these filters.' : 'No universities yet. Add your first university.'}</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="university-grid">
            {universities.slice(0, displayCount).map((uni) => (
              <div key={uni.id} className="bg-surface rounded-2xl border border-contrast/[0.07] hover:-translate-y-1 transition-all duration-200 overflow-hidden group flex flex-col h-full" data-testid={`university-card-${uni.id}`}>
                {/* Cover area */}
                <div className="relative w-full bg-sunken flex items-end overflow-hidden h-40 shrink-0">
                  <img
                    src={getImageUrl(uni.cover_image_url)}
                    alt={uni.name + " Campus"}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={e => { e.target.src = '/university-fallback.jpg'; }}
                  />
                  
                  {/* Edit / delete — appear on hover */}
                  <div className="absolute top-2 right-2 flex gap-1 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity z-20">
                    <button onClick={() => openEdit(uni)} aria-label={`Edit ${uni.name}`} className="p-1.5 bg-black/40 backdrop-blur-md text-white hover:bg-white hover:text-brand rounded-lg shadow-sm transition-colors" data-testid={`edit-university-${uni.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(uni.id)} aria-label={`Delete ${uni.name}`} className="p-1.5 bg-black/40 backdrop-blur-md text-white hover:bg-white hover:text-ember rounded-lg shadow-sm transition-colors" data-testid={`delete-university-${uni.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {/* Info strip */}
                <div className="border-t border-contrast/[0.07] p-5 flex flex-col flex-grow gap-2">
                  <h3 
                    title={uni.name}
                    className="line-clamp-2 min-h-[2.75rem] text-sm md:text-base font-semibold leading-snug tracking-tight text-ink" 
                    style={{ fontFamily: 'Outfit' }}
                  >
                    {uni.name}
                  </h3>
                  
                  <div className="flex flex-col gap-1.5">
                    <p className="flex items-center gap-1.5 text-xs text-ink-3 truncate" title={uni.city ? `${uni.city}, ${uni.country}` : uni.country}>
                      <MapPin className="w-3 h-3 shrink-0" />
                      <span className="truncate">{uni.city ? `${uni.city}, ` : ''}{uni.country}</span>
                    </p>
                    
                    {uni.ranking > 0 && (
                      <p className="flex items-center gap-1.5 text-xs text-ink-3 truncate">
                        <Trophy className="w-3 h-3 shrink-0" />
                        <span className="truncate">Rank #{uni.ranking}</span>
                      </p>
                    )}
                    
                    {uni.website && (
                      <a href={uni.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-brand hover:underline truncate" title={uni.website}>
                        <Globe className="w-3 h-3 shrink-0" />
                        <span className="truncate">{uni.website}</span>
                      </a>
                    )}
                  </div>
                  
                  <div className="mt-auto pt-3 flex flex-wrap gap-1.5">
                    {uni.stream && (
                      <span className="text-[11px] px-2 py-0.5 rounded-md bg-surface-2/80 text-ink-2 border border-contrast/10 whitespace-nowrap">
                        {uni.stream}
                      </span>
                    )}
                    {uni.courses?.length > 0 && (
                      <>
                        {uni.courses.slice(0, 3).map((c, i) => (
                          <span key={i} title={c} className="text-[11px] px-2 py-0.5 rounded-md bg-surface-2/80 text-ink-2 border border-contrast/10 truncate max-w-[120px]">
                            {c}
                          </span>
                        ))}
                        {uni.courses.length > 3 && (
                          <span className="text-[11px] px-2 py-0.5 rounded-md bg-surface-2/80 text-ink-2 border border-contrast/10">
                            +{uni.courses.length - 3}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {uni.programs_count > 0 && (
                    <button
                      onClick={() => openPrograms(uni)}
                      disabled={loadingProgramsId === uni.id}
                      data-testid={`view-programs-${uni.id}`}
                      className="mt-2 inline-flex items-center gap-1.5 self-start text-[11px] font-medium px-2.5 py-1 rounded-md bg-ember/10 text-warning border border-ember/30 hover:bg-ember/20 transition-colors disabled:opacity-60"
                    >
                      <GraduationCap className="w-3 h-3" /> {loadingProgramsId === uni.id ? 'Loading...' : `${uni.programs_count} Program${uni.programs_count === 1 ? '' : 's'}`}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {universities.length > displayCount && (
            <div className="flex justify-center pt-4 pb-8">
              <Button 
                onClick={() => setDisplayCount(prev => prev + 24)}
                className="bg-surface border border-contrast/10 shadow-card text-ink-2 hover:text-ink hover:bg-surface-2 px-8 rounded-full"
              >
                Load More Universities
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{editing ? 'Edit University' : 'Add University'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="uni-uni-name-input">University Name *</Label>
                <Input id="uni-uni-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. MIT" data-testid="uni-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="uni-uni-country-input">Country *</Label>
                <Input id="uni-uni-country-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g. USA" data-testid="uni-country-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="uni-uni-city-input">City</Label>
                <Input id="uni-uni-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Cambridge" data-testid="uni-city-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="uni-uni-ranking-input">Ranking</Label>
                <Input id="uni-uni-ranking-input" type="number" value={form.ranking} onChange={(e) => setForm({ ...form, ranking: e.target.value })} data-testid="uni-ranking-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="uni-uni-stream-input">Stream</Label>
              <Select value={form.stream || ''} onValueChange={(v) => setForm({ ...form, stream: v })}>
                <SelectTrigger id="uni-uni-stream-input" data-testid="uni-stream-input">
                  <SelectValue placeholder="Select stream..." />
                </SelectTrigger>
                <SelectContent>
                  {STREAMS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="uni-uni-courses-input">Courses (comma separated)</Label>
              <Input id="uni-uni-courses-input" value={coursesInput} onChange={(e) => setCoursesInput(e.target.value)} placeholder="Computer Science, MBA, Engineering" data-testid="uni-courses-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="uni-f549">Cover Photo</Label>
              <div className="flex flex-col gap-3 p-4 rounded-xl border border-contrast/10 bg-surface-2">
                <div className="flex items-center gap-4">
                  <input id="uni-f549" type="file" ref={coverInputRef} className="hidden" accept="image/*" onChange={handleCoverUpload} />
                  <Button variant="outline" className="w-full h-12 gap-2 bg-brand/5 hover:bg-brand/10 text-brand border-brand/30 border-dashed" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover} type="button">
                    {uploadingCover ? <div className="w-5 h-5 border-2 border-brand border-t-transparent rounded-full animate-spin" /> : <Upload className="w-5 h-5" />}
                    {uploadingCover ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-px bg-contrast/10 flex-1"></div>
                  <span className="text-[10px] text-ink-3 uppercase tracking-wider font-semibold">OR PASTE URL</span>
                  <div className="h-px bg-contrast/10 flex-1"></div>
                </div>
                <Input value={form.cover_image_url || ''} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} placeholder="https://example.com/campus-photo.jpg" data-testid="uni-cover-image-input" className="bg-sunken border-contrast/10" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="uni-uni-intake-input">Intake Months (comma separated)</Label>
              <Input id="uni-uni-intake-input" value={intakeInput} onChange={(e) => setIntakeInput(e.target.value)} placeholder="January, September" data-testid="uni-intake-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="uni-uni-website-input">Website</Label>
                <Input id="uni-uni-website-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." data-testid="uni-website-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="uni-uni-tuition-input">Tuition Range</Label>
                <Input id="uni-uni-tuition-input" value={form.tuition_range} onChange={(e) => setForm({ ...form, tuition_range: e.target.value })} placeholder="$20k-$50k/yr" data-testid="uni-tuition-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="uni-uni-requirements-input">Requirements</Label>
              <Textarea id="uni-uni-requirements-input" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} placeholder="IELTS 6.5, GPA 3.0..." data-testid="uni-requirements-input" rows={2} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="uni-uni-description-input">Description</Label>
              <Textarea id="uni-uni-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description..." data-testid="uni-description-input" rows={2} />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="uni-cancel-btn">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="uni-save-btn" className="bg-brand hover:bg-brand/90 text-white">
                {saving ? 'Saving...' : editing ? 'Update' : 'Add University'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ProgramsDialog
        open={!!programsUni}
        onOpenChange={(o) => !o && setProgramsUni(null)}
        university={programsUni}
      />
    </div>
  );
}
